/**
 * DensitySnapper
 *
 * Aligns line prompts to density features in a cached density mask.
 * Provides perpendicular snapping (position alignment) and rotation
 * scanning (direction alignment) by probing the density image.
 */

import * as THREE from 'three';
import { LinePrompt } from './types';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

/** Debug data for one pass of alignPromptToPositionAndRotation */
export interface DensitySnapperPassDebug {
  /** Probe scan lines in world space (perpendicular scan segments at each sample point) */
  probes: Array<{ start: THREE.Vector3; end: THREE.Vector3 }>;
  /** Peak positions in world space with their weights */
  peaks: Array<{ worldPoint: THREE.Vector3; weight: number }>;
  /** Fitted line endpoints in world space (if RANSAC succeeded), null otherwise */
  fittedLine: { start: THREE.Vector3; end: THREE.Vector3 } | null;
  /** World positions of RANSAC inlier peaks (subset of peaks that support the fitted line) */
  inliers: THREE.Vector3[];
}

/** Debug data from the last alignPromptToPositionAndRotation call */
export interface DensitySnapperDebugData {
  /** One entry per pass (coarse→fine) */
  passes: DensitySnapperPassDebug[];
}

/** Result from a single probe pass scan */
interface ProbePassResult {
  weightedSum: number;
  totalWeight: number;
  probeHits: number;
  numSamples: number;
  peakWorldPoints: Array<{ worldPoint: THREE.Vector3; weight: number }>;
  probeLines: Array<{ start: THREE.Vector3; end: THREE.Vector3 }>;
}

export class DensitySnapper {
  /** Enable to collect debug data for visualization */
  debugEnabled = false;
  /** Debug data from the last alignPromptToPositionAndRotation call (only populated when debugEnabled) */
  lastDebugData: DensitySnapperDebugData | null = null;

  constructor(private coordinateTransformer: CoordinateTransformer) {}

  /**
   * Pixel-align a prompt at its current worldPoint by scanning perpendicular
   * to its direction in the cached density mask.
   *
   * Runs multiple passes with decreasing search range for progressively
   * finer alignment. Each pass uses the same number of sample probes but
   * scans a smaller perpendicular window, converging on the line feature.
   * Direction is preserved (no rotation).
   */
  alignPromptToPosition(prompt: LinePrompt, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null): void {
    if (!densityMap) return;
    const t = this.coordinateTransformer;
    const frame = t.getBasePlaneFrame();

    const totalWorldLen = (prompt.positiveLength ?? 5) + (prompt.negativeLength ?? 5);
    const passRanges = [30, 20, 10];

    for (const searchRange of passRanges) {
      const result = this.runProbePass(prompt, totalWorldLen, searchRange, densityMap, positionMap, false);
      if (!result || result.probeHits < result.numSamples * 0.7 || result.totalWeight < 0.001) continue;

      // In-plane perp matches what `runProbePass` used to project the
      // weighted offset (see comment there). On floor plane: world XY
      // perp. On wall plane: vertical along the wall.
      const normal = frame ? frame.normal : new THREE.Vector3(0, 0, 1);
      const perpWorld = new THREE.Vector3().crossVectors(normal, prompt.direction).normalize();
      if (perpWorld.lengthSq() < 1e-9) perpWorld.set(-prompt.direction.y, prompt.direction.x, 0).normalize();
      const avgOffset = result.weightedSum / result.totalWeight;
      prompt.worldPoint.add(perpWorld.clone().multiplyScalar(avgOffset));

      // Pin to the base plane: with a frame, round-trip drops any
      // off-plane drift; without a frame, fall back to the legacy
      // floor-Z flatten.
      if (frame) {
        const local = t.toBasePlaneLocal(prompt.worldPoint);
        local.z = 0;
        prompt.worldPoint.copy(t.fromBasePlaneLocal(local));
      } else if (floorHeight !== null) {
        prompt.worldPoint.z = floorHeight;
      }
    }
  }

  /**
   * Like alignPromptToPosition but also rotates the prompt on the last pass
   * by fitting a line through the density peak positions found by the probes.
   * Position converges in passes 1-2 (coarse/medium), then the last pass
   * derives the wall angle from peak positions and applies it.
   */
  alignPromptToPositionAndRotation(prompt: LinePrompt, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null): void {
    if (!densityMap) return;
    const tf = this.coordinateTransformer;
    const frame = tf.getBasePlaneFrame();

    const collecting = this.debugEnabled;
    const debugPasses: DensitySnapperPassDebug[] = [];
    if (collecting) {
      this.lastDebugData = null;
    }

    const totalWorldLen = (prompt.positiveLength ?? 5) + (prompt.negativeLength ?? 5);
    const passRanges = [40, 30, 20];
    const rotationBlend = [0.3, 0.6, 1.0];

    for (let passIdx = 0; passIdx < passRanges.length; passIdx++) {
      const result = this.runProbePass(prompt, totalWorldLen, passRanges[passIdx], densityMap, positionMap, collecting);
      if (!result || result.probeHits < result.numSamples * 0.7 || result.totalWeight < 0.001) continue;

      // Position snap along the in-plane perp (`normal × dir`).
      const normal = frame ? frame.normal : new THREE.Vector3(0, 0, 1);
      const perpWorld = new THREE.Vector3().crossVectors(normal, prompt.direction).normalize();
      if (perpWorld.lengthSq() < 1e-9) perpWorld.set(-prompt.direction.y, prompt.direction.x, 0).normalize();
      const avgOffset = result.weightedSum / result.totalWeight;
      prompt.worldPoint.add(perpWorld.clone().multiplyScalar(avgOffset));

      if (frame) {
        const local = tf.toBasePlaneLocal(prompt.worldPoint);
        local.z = 0;
        prompt.worldPoint.copy(tf.fromBasePlaneLocal(local));
      } else if (floorHeight !== null) {
        prompt.worldPoint.z = floorHeight;
      }

      // RANSAC line fit + rotation: project peak world points into the
      // base-plane local frame so atan2 / cos / sin / 2D RANSAC are
      // computed in (u, v). On the floor plane this is identity; on a
      // wall plane it means the fitted angle is the wall-plane tangent
      // angle.
      let fittedLine: DensitySnapperPassDebug['fittedLine'] = null;
      let inlierWorldPoints: THREE.Vector3[] = [];
      if (result.peakWorldPoints.length >= 3) {
        const peakLocals = result.peakWorldPoints.map((p) => {
          const lp = frame ? tf.toBasePlaneLocal(p.worldPoint) : p.worldPoint;
          return { x: lp.x, y: lp.y, weight: p.weight };
        });
        const fit = this.fitLineAngle(peakLocals);
        if (fit !== null) {
          const { angle: fittedAngle, cx, cy, inliers } = fit;

          // Inliers + fitted line live in local; map back to world for
          // the debug overlay (which renders in world coords).
          inlierWorldPoints = inliers.map((p) => {
            const localPoint = new THREE.Vector3(p.x, p.y, 0);
            return frame ? tf.fromBasePlaneLocal(localPoint) : localPoint;
          });

          if (collecting) {
            const fitDirX = Math.cos(fittedAngle);
            const fitDirY = Math.sin(fittedAngle);

            let minProj = Infinity,
              maxProj = -Infinity;
            for (let i = 0; i < peakLocals.length; i++) {
              const p = peakLocals[i];
              const proj = (p.x - cx) * fitDirX + (p.y - cy) * fitDirY;
              if (proj < minProj) minProj = proj;
              if (proj > maxProj) maxProj = proj;
            }

            const extend = (maxProj - minProj) * 0.1;
            const startLocal = new THREE.Vector3(cx + fitDirX * (minProj - extend), cy + fitDirY * (minProj - extend), 0);
            const endLocal = new THREE.Vector3(cx + fitDirX * (maxProj + extend), cy + fitDirY * (maxProj + extend), 0);
            fittedLine = {
              start: frame ? tf.fromBasePlaneLocal(startLocal) : startLocal,
              end: frame ? tf.fromBasePlaneLocal(endLocal) : endLocal,
            };
          }

          // Direction blend happens in (u, v): take current direction
          // local angle, blend toward fittedAngle by the pass's rotation
          // weight, build the new local direction, map back to world.
          const dirLocal = frame ? tf.toBasePlaneLocalDir(prompt.direction) : prompt.direction;
          const currentAngle = Math.atan2(dirLocal.y, dirLocal.x);
          let angleDiff = fittedAngle - currentAngle;
          while (angleDiff > Math.PI / 2) angleDiff -= Math.PI;
          while (angleDiff < -Math.PI / 2) angleDiff += Math.PI;

          const snappedAngle = currentAngle + angleDiff * rotationBlend[passIdx];
          const newDirLocal = new THREE.Vector3(Math.cos(snappedAngle), Math.sin(snappedAngle), 0);
          const newDirWorld = frame ? tf.fromBasePlaneLocalDir(newDirLocal) : newDirLocal;
          prompt.direction.copy(newDirWorld.normalize());
        }
      }

      if (collecting) {
        debugPasses.push({
          probes: result.probeLines,
          peaks: result.peakWorldPoints,
          fittedLine,
          inliers: inlierWorldPoints,
        });
      }
    }

    if (collecting) {
      this.lastDebugData = { passes: debugPasses };
    }
  }

  /**
   * Render debug data from the last alignPromptToPositionAndRotation call
   * into the given THREE scene. Only shows the last pass. Peaks are shown as
   * points colored by weight (blue=low, red=high). Probes are shown as thin
   * lines. Fitted line shown in green.
   */
  renderDebug(scene: THREE.Scene, floorHeight: number | null): void {
    this.clearDebug(scene);
    if (!this.lastDebugData || this.lastDebugData.passes.length === 0) return;

    // Lift debug geometry slightly above the base plane along the plane
    // normal so it renders just in front of the cloud / shape. With a
    // frame: lift by 5cm along the frame's normal direction. Without a
    // frame: legacy behavior — lift Z to `floorHeight + 0.05`.
    const frame = this.coordinateTransformer.getBasePlaneFrame();
    const lift = (p: { x: number; y: number; z?: number }): [number, number, number] => {
      if (frame) {
        const px = p.x + frame.normal.x * 0.05;
        const py = p.y + frame.normal.y * 0.05;
        const pz = (p.z ?? 0) + frame.normal.z * 0.05;
        return [px, py, pz];
      }
      const z = floorHeight !== null ? floorHeight + 0.05 : 0.05;
      return [p.x, p.y, z];
    };
    const pass = this.lastDebugData.passes[this.lastDebugData.passes.length - 1];

    // Render probe scan lines as LineSegments
    if (pass.probes.length > 0) {
      const probePositions: number[] = [];
      for (const probe of pass.probes) {
        probePositions.push(...lift(probe.start));
        probePositions.push(...lift(probe.end));
      }
      const probeGeom = new THREE.BufferGeometry();
      probeGeom.setAttribute('position', new THREE.Float32BufferAttribute(probePositions, 3));
      const probeMat = new THREE.LineBasicMaterial({
        color: 0xffff00,
        linewidth: 1,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.4,
      });
      const probeLines = new THREE.LineSegments(probeGeom, probeMat);
      probeLines.renderOrder = 999;
      probeLines.userData._densitySnapDebug = true;
      scene.add(probeLines);
    }

    // Render RANSAC inliers as larger green squares (behind weight-colored peaks)
    if (pass.inliers.length > 0) {
      const inlierPositions: number[] = [];
      for (const p of pass.inliers) {
        inlierPositions.push(...lift(p));
      }
      const inlierGeom = new THREE.BufferGeometry();
      inlierGeom.setAttribute('position', new THREE.Float32BufferAttribute(inlierPositions, 3));
      const inlierMat = new THREE.PointsMaterial({
        color: 0x00ff00,
        size: 12,
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
      });
      const inlierPoints = new THREE.Points(inlierGeom, inlierMat);
      inlierPoints.renderOrder = 1000;
      inlierPoints.userData._densitySnapDebug = true;
      scene.add(inlierPoints);
    }

    // Render peaks as Points with vertex colors (weight-based, on top of inliers)
    if (pass.peaks.length > 0) {
      const minWeight = pass.peaks.reduce((m, p) => Math.min(m, p.weight), Infinity);
      const maxWeight = pass.peaks.reduce((m, p) => Math.max(m, p.weight), -Infinity);
      const weightRange = maxWeight - minWeight;
      const positions: number[] = [];
      const colors: number[] = [];

      for (const peak of pass.peaks) {
        positions.push(...lift(peak.worldPoint));
        const norm = weightRange > 0.001 ? (peak.weight - minWeight) / weightRange : 1;
        colors.push(norm, 0, 1 - norm); // blue (low weight) → red (high weight)
      }

      const peakGeom = new THREE.BufferGeometry();
      peakGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      peakGeom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const peakMat = new THREE.PointsMaterial({
        size: 6,
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
        vertexColors: true,
      });

      const peakPoints = new THREE.Points(peakGeom, peakMat);
      peakPoints.renderOrder = 1001;
      peakPoints.userData._densitySnapDebug = true;
      scene.add(peakPoints);
    }

    // Render fitted line in green
    if (pass.fittedLine) {
      const s = lift(pass.fittedLine.start);
      const e = lift(pass.fittedLine.end);
      const lineGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(s[0], s[1], s[2]), new THREE.Vector3(e[0], e[1], e[2])]);
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x00ff00,
        linewidth: 2,
        depthTest: false,
        depthWrite: false,
      });
      const line = new THREE.Line(lineGeom, lineMat);
      line.renderOrder = 1002;
      line.userData._densitySnapDebug = true;
      scene.add(line);
    }
  }

  /**
   * Remove all debug objects from the scene.
   */
  clearDebug(scene: THREE.Scene): void {
    const toRemove: THREE.Object3D[] = [];
    scene.traverse((obj) => {
      if (obj.userData._densitySnapDebug) {
        toRemove.push(obj);
      }
    });
    for (const obj of toRemove) {
      scene.remove(obj);
      if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry.dispose();
      if ((obj as THREE.Mesh).material) {
        const mat = (obj as THREE.Mesh).material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    }
  }

  /**
   * Fit a line through weighted pixel-space points using weighted RANSAC.
   * Picks pairs of points, fits a line, scores by total weight of inliers
   * (points within a perpendicular distance threshold). Returns the angle
   * and centroid of the best-supported line, or null if no good fit is found.
   */
  private fitLineAngle(points: Array<{ x: number; y: number; weight: number }>): { angle: number; cx: number; cy: number; inliers: Array<{ x: number; y: number }> } | null {
    if (points.length < 2) return null;

    const inlierThreshold = 0.005; // 1cm in world space
    const maxIterations = Math.min((points.length * (points.length - 1)) / 2, 50);

    let bestAngle = 0;
    let bestScore = -1;
    let bestInliers: Array<{ x: number; y: number; weight: number }> = [];

    // Build cumulative weight array for weighted random sampling
    // Higher-weight points are more likely to be selected as RANSAC pair candidates
    const totalPointWeight = points.reduce((sum, p) => sum + p.weight, 0);
    const cumulativeWeights: number[] = [];
    let cumSum = 0;
    for (const p of points) {
      cumSum += p.weight;
      cumulativeWeights.push(cumSum / totalPointWeight);
    }
    const weightedRandomIndex = (): number => {
      const r = Math.random();
      for (let k = 0; k < cumulativeWeights.length; k++) {
        if (r <= cumulativeWeights[k]) return k;
      }
      return cumulativeWeights.length - 1;
    };

    // Sample pairs weighted by point weight
    for (let iterations = 0; iterations < maxIterations; iterations++) {
      const i = weightedRandomIndex();
      let j = weightedRandomIndex();
      // Ensure distinct points
      if (j === i) {
        j = (i + 1) % points.length;
      }
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 0.005) continue; // skip near-duplicate points

      // Line direction and perpendicular
      const ndx = dx / len;
      const ndy = dy / len;

      // Score: sum of weights of inlier points
      let score = 0;
      const inliers: Array<{ x: number; y: number; weight: number }> = [];
      for (const p of points) {
        // Perpendicular distance from point to the line through points[i] and points[j]
        const toPx = p.x - points[i].x;
        const toPy = p.y - points[i].y;
        const perpDist = Math.abs(toPx * -ndy + toPy * ndx);
        if (perpDist <= inlierThreshold) {
          score += p.weight;
          inliers.push(p);
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestAngle = Math.atan2(ndy, ndx);
        bestInliers = inliers;
      }
    }

    if (bestScore <= 0 || bestInliers.length < 2) return null;

    // Compute weighted centroid of inliers
    let cx = 0,
      cy = 0,
      tw = 0;
    for (const p of bestInliers) {
      cx += p.x * p.weight;
      cy += p.y * p.weight;
      tw += p.weight;
    }
    cx /= tw;
    cy /= tw;

    return { angle: bestAngle, cx, cy, inliers: bestInliers.map((p) => ({ x: p.x, y: p.y })) };
  }

  /**
   * Snap a prompt's rotation to 90-degree angles relative to the closest neighbor edge.
   * Finds the spatially closest neighbor by distance and snaps to the nearest 90° multiple
   * of that neighbor's direction. Then performs perpendicular position snapping.
   */
  alignPromptToClosestEdge(prompt: LinePrompt, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null, neighbors: Array<{ position: THREE.Vector3; direction: THREE.Vector3 }>): void {
    if (neighbors.length === 0) return;
    const tf = this.coordinateTransformer;
    const frame = tf.getBasePlaneFrame();

    // Find the closest neighbor by distance to the prompt's current position.
    // Distance is preserved by the orthonormal frame transform, so we keep this in world.
    let closestNeighbor = neighbors[0];
    let closestDistance = prompt.worldPoint.distanceTo(neighbors[0].position);

    for (let i = 1; i < neighbors.length; i++) {
      const dist = prompt.worldPoint.distanceTo(neighbors[i].position);
      if (dist < closestDistance) {
        closestDistance = dist;
        closestNeighbor = neighbors[i];
      }
    }

    // Reference angles are 2D angles in (u, v) — atan2 of the in-plane
    // components. On the floor plane this equals atan2 of world (x, y).
    const refDirLocal = frame ? tf.toBasePlaneLocalDir(closestNeighbor.direction) : closestNeighbor.direction;
    const currDirLocal = frame ? tf.toBasePlaneLocalDir(prompt.direction) : prompt.direction;
    const refAngle = Math.atan2(refDirLocal.y, refDirLocal.x);
    const currentAngle = Math.atan2(currDirLocal.y, currDirLocal.x);

    let bestSnapAngle = refAngle;
    let smallestAngleDiff = Infinity;

    for (let i = 0; i < 4; i++) {
      const snapAngle = refAngle + (i * Math.PI) / 2;
      let angleDiff = currentAngle - snapAngle;
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      const absAngleDiff = Math.abs(angleDiff);
      if (absAngleDiff < smallestAngleDiff) {
        smallestAngleDiff = absAngleDiff;
        bestSnapAngle = snapAngle;
      }
    }

    // Snapped angle is in (u, v); build local direction, map to world.
    const newDirLocal = new THREE.Vector3(Math.cos(bestSnapAngle), Math.sin(bestSnapAngle), 0);
    const newDirWorld = frame ? tf.fromBasePlaneLocalDir(newDirLocal) : newDirLocal;
    prompt.direction.copy(newDirWorld.normalize());

    if (densityMap) {
      this.alignPromptToPosition(prompt, densityMap, positionMap, floorHeight);
    }
  }

  /**
   * Snap a prompt's rotation to be perpendicular to a given reference direction.
   * Only considers the two perpendicular angles (+90° and -90°), never parallel.
   * Then performs perpendicular position snapping.
   */
  alignPromptToAngle(prompt: LinePrompt, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null, referenceDirection: THREE.Vector3): void {
    const tf = this.coordinateTransformer;
    const frame = tf.getBasePlaneFrame();

    // Angles are read in (u, v).
    const refDirLocal = frame ? tf.toBasePlaneLocalDir(referenceDirection) : referenceDirection;
    const currDirLocal = frame ? tf.toBasePlaneLocalDir(prompt.direction) : prompt.direction;
    const refAngle = Math.atan2(refDirLocal.y, refDirLocal.x);
    const currentAngle = Math.atan2(currDirLocal.y, currDirLocal.x);

    // Only consider perpendicular angles (+90° and -90°), not parallel
    const perpAngles = [refAngle + Math.PI / 2, refAngle - Math.PI / 2];

    let bestSnapAngle = perpAngles[0];
    let smallestAngleDiff = Infinity;

    for (const snapAngle of perpAngles) {
      let angleDiff = currentAngle - snapAngle;
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      const absAngleDiff = Math.abs(angleDiff);
      if (absAngleDiff < smallestAngleDiff) {
        smallestAngleDiff = absAngleDiff;
        bestSnapAngle = snapAngle;
      }
    }

    const newDirLocal = new THREE.Vector3(Math.cos(bestSnapAngle), Math.sin(bestSnapAngle), 0);
    const newDirWorld = frame ? tf.fromBasePlaneLocalDir(newDirLocal) : newDirLocal;
    prompt.direction.copy(newDirWorld.normalize());

    if (densityMap) {
      this.alignPromptToPosition(prompt, densityMap, positionMap, floorHeight);
    }
  }

  /**
   * Run a single probe pass: scan perpendicular probes along the prompt line,
   * find density peaks, decode position map offsets, and accumulate weighted results.
   * Shared by alignPromptToPosition and alignPromptToPositionAndRotation.
   */
  private runProbePass(prompt: LinePrompt, totalWorldLen: number, searchRange: number, densityMap: ImageData, positionMap: ImageData | null, collectDebug: boolean): ProbePassResult | null {
    const dirWorld = prompt.direction;
    // In-plane perpendicular: `normal × direction`. On the world XY
    // floor plane (normal = +Z) this reduces to `(-dir.y, dir.x, 0)`,
    // matching the pre-frame behavior. On a vertical wall plane
    // (normal = horizontal perpendicular) this evaluates to the
    // vertical direction along the wall — the correct in-plane perp.
    const frame = this.coordinateTransformer.getBasePlaneFrame();
    const normal = frame ? frame.normal : new THREE.Vector3(0, 0, 1);
    const perpWorld = new THREE.Vector3().crossVectors(normal, dirWorld).normalize();
    // Fallback when direction is parallel to normal (degenerate prompt):
    // use the legacy XY perpendicular so the snap still produces a
    // sensible result instead of NaNs.
    if (perpWorld.lengthSq() < 1e-9) {
      perpWorld.set(-dirWorld.y, dirWorld.x, 0).normalize();
    }

    const maskSpace0 = this.coordinateTransformer.worldToMaskSpace(prompt.worldPoint);
    const endWorld0 = prompt.worldPoint.clone().add(dirWorld.clone().multiplyScalar(totalWorldLen));
    const endMask0 = this.coordinateTransformer.worldToMaskSpace(endWorld0);
    const totalPixelLen = Math.sqrt(((endMask0.x - maskSpace0.x) * densityMap.width) ** 2 + ((endMask0.y - maskSpace0.y) * densityMap.height) ** 2);

    const halfPixelLen = totalPixelLen / 2;
    const numSamples = Math.max(5, Math.floor(totalPixelLen / 3));
    const pixelsPerWorldUnit = totalWorldLen > 0 ? totalPixelLen / totalWorldLen : 1;
    const padding = Math.min(0.05 * pixelsPerWorldUnit, totalPixelLen * 0.125); // 5cm hard threshold, max 12.5% per side (25% total)
    const samplingLen = totalPixelLen - 2 * padding;
    if (samplingLen <= 0) return null;

    const maskSpace = this.coordinateTransformer.worldToMaskSpace(prompt.worldPoint);
    const centerPixelX = maskSpace.x * densityMap.width;
    const centerPixelY = maskSpace.y * densityMap.height;

    const offsetWorld = prompt.worldPoint.clone().add(dirWorld.clone().multiplyScalar(1.0));
    const offsetMask = this.coordinateTransformer.worldToMaskSpace(offsetWorld);
    const dirPixelX = offsetMask.x * densityMap.width - centerPixelX;
    const dirPixelY = offsetMask.y * densityMap.height - centerPixelY;
    const dirLen = Math.sqrt(dirPixelX * dirPixelX + dirPixelY * dirPixelY);
    if (dirLen < 0.001) return null;

    const lineDirX = dirPixelX / dirLen;
    const lineDirY = dirPixelY / dirLen;
    const perpDirX = -lineDirY;
    const perpDirY = lineDirX;

    let weightedSum = 0;
    let totalWeight = 0;
    let probeHits = 0;
    const peakWorldPoints: Array<{ worldPoint: THREE.Vector3; weight: number }> = [];
    const probeLines: Array<{ start: THREE.Vector3; end: THREE.Vector3 }> = [];

    for (let i = 0; i < numSamples; i++) {
      const t = numSamples === 1 ? 0.5 : i / (numSamples - 1);
      const dist = -halfPixelLen + padding + t * samplingLen;
      const sampleX = centerPixelX + lineDirX * dist;
      const sampleY = centerPixelY + lineDirY * dist;

      if (collectDebug) {
        const probeStartWorld = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2((sampleX + perpDirX * -searchRange) / densityMap.width, (sampleY + perpDirY * -searchRange) / densityMap.height));
        const probeEndWorld = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2((sampleX + perpDirX * searchRange) / densityMap.width, (sampleY + perpDirY * searchRange) / densityMap.height));
        probeLines.push({ start: probeStartWorld, end: probeEndWorld });
      }

      const peak = this.findDensityPeak(sampleX, sampleY, perpDirX, perpDirY, searchRange, densityMap);
      if (!peak) continue;

      probeHits++;

      let offset: number;
      let weight = 0.5;

      if (positionMap) {
        const px = Math.round(peak.x);
        const py = Math.round(peak.y);
        if (px >= 0 && px < positionMap.width && py >= 0 && py < positionMap.height) {
          const idx = (py * positionMap.width + px) * 4;
          const r = positionMap.data[idx];
          const g = positionMap.data[idx + 1];
          const b = positionMap.data[idx + 2];
          const a = positionMap.data[idx + 3];

          const densityIdx = (py * densityMap.width + px) * 4;
          const density = (densityMap.data[densityIdx] + densityMap.data[densityIdx + 1] + densityMap.data[densityIdx + 2]) / 3;
          const baseWeight = density / 256;
          // Bonus for points far from the camera. With the new eye-space
          // encoding, B is normalized eye-Z depth — high B means far from
          // the camera. For top-down floor captures, far = floor-level
          // points (where walls meet the floor), the same intent as the
          // pre-eye-space heuristic.
          weight = baseWeight + (baseWeight * b) / 256;

          // Decode eye-space XYZ from the encoded RGBA and convert to world
          // via the cached inverse view matrix. Works for any camera
          // orientation (no plane-projection assumption).
          const decodedWorld = this.coordinateTransformer.decodePositionMapPixel(r, g, b, a);
          if (decodedWorld) {
            const toDecoded = new THREE.Vector3().subVectors(decodedWorld, prompt.worldPoint);
            offset = toDecoded.dot(perpWorld);
            weightedSum += offset * weight;
            totalWeight += weight;
            peakWorldPoints.push({ worldPoint: decodedWorld, weight });
            continue;
          }
        }
      }

      // Fallback: convert peak pixel position to world
      const dx = peak.x - sampleX;
      const dy = peak.y - sampleY;
      const pixelOffset = dx * perpDirX + dy * perpDirY;
      const shiftedMask = new THREE.Vector2((centerPixelX + perpDirX * pixelOffset) / densityMap.width, (centerPixelY + perpDirY * pixelOffset) / densityMap.height);
      const shiftedWorld = this.coordinateTransformer.maskSpaceToWorld(shiftedMask);
      const toShifted = new THREE.Vector3().subVectors(shiftedWorld, prompt.worldPoint);
      offset = toShifted.dot(perpWorld);
      weightedSum += offset * weight;
      totalWeight += weight;

      const peakWorld = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2(peak.x / densityMap.width, peak.y / densityMap.height));
      peakWorldPoints.push({ worldPoint: peakWorld, weight });
    }

    return { weightedSum, totalWeight, probeHits, numSamples, peakWorldPoints, probeLines };
  }

  /**
   * Find the density peak along a perpendicular scan line in the density mask.
   * Scans from -searchRange to +searchRange pixels perpendicular to a sample point.
   * Returns the pixel position of the strongest peak closest to the edge, or null.
   */
  private findDensityPeak(sampleX: number, sampleY: number, perpDirX: number, perpDirY: number, searchRange: number, densityMask: ImageData): { x: number; y: number } | null {
    const intensityThreshold = 30;
    const smoothingRadius = 2;

    // Build intensity profile
    const profile: Array<{ x: number; y: number; offset: number; intensity: number }> = [];
    const step = 0.5; // Sub-pixel sampling for finer resolution
    for (let offset = -searchRange; offset <= searchRange; offset += step) {
      const px = Math.round(sampleX + perpDirX * offset);
      const py = Math.round(sampleY + perpDirY * offset);
      if (px < 0 || px >= densityMask.width || py < 0 || py >= densityMask.height) {
        profile.push({ x: px, y: py, offset, intensity: 0 });
        continue;
      }
      const idx = (py * densityMask.width + px) * 4;
      const intensity = (densityMask.data[idx] + densityMask.data[idx + 1] + densityMask.data[idx + 2]) / 3;
      profile.push({ x: px, y: py, offset, intensity });
    }

    if (profile.length === 0) return null;

    // Smooth with triangle kernel
    const smoothed: number[] = [];
    for (let i = 0; i < profile.length; i++) {
      let weightedSum = 0,
        totalWeight = 0;
      for (let j = -smoothingRadius; j <= smoothingRadius; j++) {
        const idx = i + j;
        if (idx >= 0 && idx < profile.length) {
          const weight = smoothingRadius + 1 - Math.abs(j);
          weightedSum += profile[idx].intensity * weight;
          totalWeight += weight;
        }
      }
      smoothed.push(totalWeight > 0 ? weightedSum / totalWeight : 0);
    }

    // Find peaks
    const peaks: Array<{ idx: number; intensity: number }> = [];
    for (let i = 1; i < smoothed.length - 1; i++) {
      if (smoothed[i] >= intensityThreshold && smoothed[i] >= smoothed[i - 1] && smoothed[i] >= smoothed[i + 1]) {
        peaks.push({ idx: i, intensity: smoothed[i] });
      }
    }

    if (peaks.length === 0) {
      // Fallback: global max
      let maxVal = 0,
        maxIdx = -1;
      for (let i = 0; i < smoothed.length; i++) {
        if (smoothed[i] > maxVal && smoothed[i] >= intensityThreshold) {
          maxVal = smoothed[i];
          maxIdx = i;
        }
      }
      if (maxIdx === -1) return null;
      peaks.push({ idx: maxIdx, intensity: maxVal });
    }

    // Select best peak: strongest above 60% significance, prefer closest to center
    const maxPeakIntensity = Math.max(...peaks.map((p) => p.intensity));
    const strongPeaks = peaks.filter((p) => p.intensity >= maxPeakIntensity * 0.6);
    const centerIdx = Math.floor(profile.length / 2);
    const best = strongPeaks.reduce((closest, peak) => (Math.abs(peak.idx - centerIdx) < Math.abs(closest.idx - centerIdx) ? peak : closest));

    return { x: profile[best.idx].x, y: profile[best.idx].y };
  }
}
